import { FC } from 'react';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import FullSegment from 'components/FlightInfoFullSegment';
import Endpoint from 'components/FlightInfoEndpoint';
import SegmentInfo from 'components/FlightInfoSegmentInfo';

import { FlightInfo_segments$key } from '__generated__/FlightInfo_segments.graphql';
import { duration, durationFromString } from 'lib';

type Props = {
  segments: FlightInfo_segments$key;
};

const section = {
  borderRight: '1px solid rgba(0, 0, 0, 0.2)',
};

const panel = {
  p: 1,
};

const FlightInfo: FC<Props> = props => {
  const segments = useFragment(
    graphql`
      fragment FlightInfo_segments on FlightSegment @relay(plural: true) {
        arrivalDatetime
        departDatetime
        duration

        ...FlightInfoFullSegment_segment
        ...FlightInfoEndpoint_segment
        ...FlightInfoSegmentInfo_segment
      }
    `,
    props.segments
  );

  const connectingFlight = segments.length >= 2;

  return (
    <Box sx={{ mb: 1 }}>
      <Paper>
        <Grid container>
          <Grid item xs={4} sx={section}>
            <Box sx={panel}>
              {connectingFlight ? (
                <FullSegment segment={segments[0]} />
              ) : (
                <Endpoint variant="forward" segment={segments[0]} />
              )}

              <Divider />

              <SegmentInfo segment={segments[0]} />
            </Box>
          </Grid>

          <Grid item xs={4} sx={section}>
            <Box
              sx={{
                p: 1,
                display: 'flex',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                height: '100%',
              }}>
              {connectingFlight ? (
                <>
                  <Typography variant="subtitle2">
                    {segments.length === 2
                      ? 'Persėdimas '
                      : `${segments.length - 1} persėdimai `}
                    tarp skrydžių
                  </Typography>

                  <Typography variant="caption">
                    Persėdimo laikas{' '}
                    {duration(
                      segments[0].arrivalDatetime,
                      segments[segments.length - 1].departDatetime
                    )}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2">
                    Tiesioginis skrydis
                  </Typography>
                  <Typography variant="caption">
                    Skrydžio trukmė {durationFromString(segments[0].duration)}
                  </Typography>
                </>
              )}
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box sx={panel}>
              {connectingFlight ? (
                <FullSegment segment={segments[segments.length - 1]} />
              ) : (
                <Endpoint variant="return" segment={segments[0]} />
              )}

              <Divider />

              <SegmentInfo
                segment={
                  connectingFlight ? segments[segments.length - 1] : segments[0]
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default FlightInfo;
